'use client';

import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import { ExplorerBoundary } from './explorer-boundary';

/**
 * Defers the explorer chunk (dataset + visx runtime) until the section nears
 * the viewport. The server-rendered snapshot stays up as the Suspense
 * fallback, so nothing flashes while the chunk loads; the explorer never
 * renders on the server (inView is false at SSR time).
 */
const LazyExplorer = lazy(() =>
  import('./explorer').then((mod) => ({ default: mod.ReliabilityExplorer }))
);

export function ExplorerLazy({ snapshot }: { snapshot: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {inView ? (
        <ExplorerBoundary fallback={snapshot}>
          <Suspense fallback={snapshot}>
            <LazyExplorer />
          </Suspense>
        </ExplorerBoundary>
      ) : (
        snapshot
      )}
    </div>
  );
}
